import style from './MarqueeStrip.css?raw';

import { inlineSize, prefersReducedMotion, restartAnimation } from '../../utilities/dom.js';
import { nextMacroTask } from '../../utilities/queue.js';

const template = document.createElement('template');
template.innerHTML = /* html */`
  <style>
    ${style}
  </style>

  <div class="viewport">
    <div class="track">
      <slot></slot>
    </div>
  </div>
`;

/**
 * @customElement marquee-strip
 */
export class MarqueeStrip extends HTMLElement {

  /**
   * @param {string} [tagName]
   * @param {CustomElementRegistry} [registry]
   * @returns {typeof MarqueeStrip}
   */
  static define(tagName = 'marquee-strip', registry = customElements) {
    registry.define(tagName, MarqueeStrip);
    return this;
  }

  /**
   * @type {number}
   */
  static DEFAULT_SPEED = 100;

  /**
   * @type {HTMLElement}
   */
  #viewport;

  /**
   * @type {HTMLElement}
   */
  #track;

  /**
   * @type {?Promise<void>}
   */
  #updatePending = null;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeObserver(() => this.requestUpdate());

  constructor() {
    super();

    const root = this.attachShadow({ mode: 'open' });
    root.appendChild(template.content.cloneNode(true));

    this.#viewport = /** @type {HTMLDivElement} */ (root.querySelector('.viewport'));
    this.#track = /** @type {HTMLDivElement} */ (root.querySelector('.track'));
  }

  /**
   * @attribute
   * @returns {number}
   */
  get speed() {
    const value = Number(this.getAttribute('speed'));

    if (! Number.isFinite(value) || value <= 0) {
      return MarqueeStrip.DEFAULT_SPEED;
    }

    return value;
  }

  /**
   * @param {number} value
   */
  set speed(value) {
    this.setAttribute('speed', String(value));
    this.requestUpdate();
  }

  /**
   * @attribute
   * @returns {boolean}
   */
  get reverse() {
    return this.hasAttribute('reverse');
  }

  /**
   * @param {?boolean} value
   */
  set reverse(value) {
    this.toggleAttribute('reverse', Boolean(value));
  }

  /**
   * @returns {Element[]}
   */
  get #originals() {
    return Array.from(this.children).filter((el) => !el.hasAttribute('data-marquee-clone'));
  }

  /**
   * @returns {Element[]}
   */
  get #clones() {
    return Array.from(this.children).filter((el) => el.hasAttribute('data-marquee-clone'));
  }

  /**
   * @returns {void}
   */
  connectedCallback() {
    if (! prefersReducedMotion()) {
      this.#resizeObserver.observe(this.#track);
    }
  }

  /**
   * @returns {void}
   */
  disconnectedCallback() {
    this.#resizeObserver.unobserve(this.#track);
    this.#clones.forEach((clone) => clone.remove());
  }

  /**
   * @returns {Promise<void>}
   */
  async requestUpdate() {
    return this.#updatePending ??= nextMacroTask().then(async () => {
      await this.update();
      this.#updatePending = null;
    });
  }

  /**
   * @returns {Promise<void>}
   */
  async update() {
    const vi = inlineSize(this.#viewport);

    if (vi === 0) {
      return;
    }

    const gap = parseFloat(getComputedStyle(this.#track).columnGap);
    const size = inlineSize(this.#track);

    if (size === 0) {
      return;
    }

    const originals = this.#originals;
    const clones = this.#clones;

    const existing = Math.floor(clones.length / originals.length);

    const distance = (size + gap) / (existing + 1);
    const duration = distance / this.speed;

    const ideal = Math.ceil(vi / distance);
    const required = ideal + 1;

    const missing = required - existing;

    if (missing > 0) {
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < missing; i++) {
        fragment.append(...originals.map((element) => this.#createClone(element)));
      }

      this.append(fragment);
    } else if (missing < 0) {
      clones
        .slice(missing * originals.length)
        .forEach((el) => el.remove());
    }

    this.style.setProperty('--marquee-distance', `${distance}px`);
    this.style.setProperty('--marquee-duration', `${duration}s`);

    restartAnimation(this.#track);
  }

  /**
   * @param {HTMLElement} element
   * @returns {HTMLElement}
   */
  #createClone(element) {
    const clone = /** @type {HTMLElement} */ (element.cloneNode(true));
    clone.setAttribute('data-marquee-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    return clone;
  }
}
